import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { getToastStateFromLocationState } from "@/lib/navigation";
import { useToast, confirmAsync } from "@/lib/ui";
import { BackButton } from "@/components/BackButton";
import { parseApiError } from "@/lib/error-handling";
import {
  useAdminGroup,
  useAdminGroupUpdate,
  useAdminGroupAddMember,
  useAdminGroupRemoveMember,
  useAdminUsersList,
  type AdminGroupDetail,
  type AdminUserListItem,
} from "@/lib/admin-api";

type GroupMember = {
  id: string;
  userId: string;
  groupId: string;
  seq: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
  };
};

export function GroupDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [addMemberDialogVisible, setAddMemberDialogVisible] = useState(false);
  const [group, setGroup] = useState<Partial<AdminGroupDetail>>({
    name: "",
    type: "Primary",
  });
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);

  const groupQuery = useAdminGroup(id);
  const groupData = (groupQuery.data as { group?: AdminGroupDetail } | undefined)?.group ?? null;
  const members = (groupData?.members ?? []) as GroupMember[];

  // Fetch all users for the add member dialog
  // Note: API has a max pageSize of 100, so we fetch the first 100 users
  const usersQuery = useAdminUsersList({
    page: 1,
    pageSize: 100,
    sortField: "firstName",
    sortOrder: 1,
    filterText: "",
  });
  
  // Handle the response structure - check both possible formats
  const responseData = usersQuery.data as any;
  const allUsers = (responseData?.data ?? []) as AdminUserListItem[];
  
  // Debug: log the response to understand the structure
  if (process.env.NODE_ENV === "development") {
    if (usersQuery.data && allUsers.length === 0) {
      console.log("Users query response:", usersQuery.data);
      console.log("Response data:", responseData);
    }
  }

  // Filter out users who are already members
  const availableUsers = allUsers.filter(
    (user) => user && user.id && !members.some((member) => member.userId === user.id),
  );

  // Check if users query has data loaded
  const isUsersLoading = usersQuery.isLoading || usersQuery.isFetching;
  const hasUsersError = usersQuery.isError;

  const groupUpdate = useAdminGroupUpdate();
  const addMember = useAdminGroupAddMember();
  const removeMember = useAdminGroupRemoveMember();

  useEffect(() => {
    if (!id) {
      void navigate("/groups");
    }
  }, [id, navigate]);

  // Show toast from router state
  useEffect(() => {
    if (groupQuery.isLoading) return;

    const toastState = getToastStateFromLocationState(location.state);
    if (!toastState) return;

    showToast(toastState);
    void navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.pathname,
    location.state,
    navigate,
    groupQuery.isLoading,
    showToast,
  ]);

  const handleEdit = () => {
    if (groupData) {
      setGroup({
        name: groupData.name,
        type: groupData.type,
      });
      setEditDialogVisible(true);
    }
  };

  const handleSave = async () => {
    if (!id || !group.name?.trim() || !group.type) {
      setSubmitted(true);
      return;
    }

    try {
      await groupUpdate.mutateAsync({
        params: { path: { id } },
        body: {
          name: group.name,
          type: group.type,
        },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: "Group updated",
        life: 3000,
      });
      setEditDialogVisible(false);
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to update group",
        life: 3000,
      });
    }
  };

  const handleAddMember = async () => {
    if (!id || !selectedUserId) {
      setSubmitted(true);
      return;
    }

    try {
      await addMember.mutateAsync({
        params: { path: { id } },
        body: {
          userId: selectedUserId,
        },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: "Member added to group",
        life: 3000,
      });
      setAddMemberDialogVisible(false);
      setSelectedUserId("");
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to add member",
        life: 3000,
      });
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    const confirmed = await confirmAsync({
      message: `Are you sure you want to remove ${member.user.firstName} ${member.user.lastName} from this group?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
    });

    if (!confirmed || !id) return;

    try {
      await removeMember.mutateAsync({
        params: {
          path: {
            id,
            userId: member.userId,
          },
        },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: "Member removed from group",
        life: 3000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to remove member",
        life: 3000,
      });
    }
  };

  if (!groupData) {
    return (
      <div className="card">
        <p>Loading...</p>
      </div>
    );
  }

  const nameBodyTemplate = (rowData: GroupMember) => {
    return `${rowData.user.firstName} ${rowData.user.lastName}`;
  };

  const emailBodyTemplate = (rowData: GroupMember) => {
    return rowData.user.email || "-";
  };

  const typeBodyTemplate = () => {
    // Membership type is determined by the group type
    return <Tag value={groupData?.type || "-"} />;
  };

  const actionBodyTemplate = (rowData: GroupMember) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => navigate(`/users/${rowData.user.id}`)}
          tooltip="View User"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-danger p-button-sm"
          onClick={() => {
            void handleRemoveMember(rowData);
          }}
          tooltip="Remove"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const userOptions = availableUsers.map((user) => ({
    label: `${user.firstName} ${user.lastName}${user.email ? ` (${user.email})` : ""}`,
    value: user.id,
  }));


  const editDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setEditDialogVisible(false)}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void handleSave();
        }}
      />
    </>
  );

  const addMemberDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          setAddMemberDialogVisible(false);
          setSelectedUserId("");
        }}
      />
      <Button
        label="Add"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void handleAddMember();
        }}
        disabled={availableUsers.length === 0 || isUsersLoading}
      />
    </>
  );

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <div className="mb-4">
        <div className="flex align-items-center gap-3 mb-4">
          <BackButton />
          <h1 className="text-2xl font-bold m-0">{groupData.name}</h1>
        </div>
      </div>

      <Panel header="Group Information" className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="mb-3">
              <strong>Name:</strong> {groupData.name}
            </div>
            <div className="mb-3">
              <strong>Type:</strong> <Tag value={groupData.type} />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            label="Edit Group"
            icon="pi pi-pencil"
            onClick={handleEdit}
          />
        </div>
      </Panel>

      <Panel header="Members" className="mb-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div>
            <strong>{members.length}</strong> member{members.length !== 1 ? "s" : ""}
          </div>
          <Button
            label="Add Member"
            icon="pi pi-plus"
            onClick={() => {
              setAddMemberDialogVisible(true);
              setSubmitted(false);
            }}
            loading={isUsersLoading}
          />
        </div>
        <DataTable
          value={members}
          responsiveLayout="scroll"
          emptyMessage="No members found"
          className="text-sm"
        >
          <Column
            header="Name"
            body={nameBodyTemplate}
            style={{ minWidth: "200px" }}
          />
          <Column
            header="Email"
            body={emailBodyTemplate}
            style={{ minWidth: "250px" }}
          />
          <Column
            header="Type"
            body={typeBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ minWidth: "150px" }}
          />
        </DataTable>
      </Panel>

      <Dialog
        visible={editDialogVisible}
        style={{ width: "450px" }}
        header="Edit Group"
        modal
        className="p-fluid"
        footer={editDialogFooter}
        onHide={() => setEditDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="name">Name</label>
          <InputText
            id="name"
            value={group.name}
            onChange={(e) => setGroup({ ...group, name: e.target.value })}
            required
            autoFocus
            className={submitted && !group.name ? "p-invalid" : ""}
          />
          {submitted && !group.name && (
            <small className="p-error">Name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="type">Type</label>
          <Dropdown
            id="type"
            value={group.type}
            options={[
              { label: "Primary", value: "Primary" },
              { label: "Secondary", value: "Secondary" },
            ]}
            onChange={(e) => setGroup({ ...group, type: e.value })}
            placeholder="Select Type"
            className={submitted && !group.type ? "p-invalid" : ""}
          />
          {submitted && !group.type && (
            <small className="p-error">Type is required.</small>
          )}
        </div>
      </Dialog>

      <Dialog
        visible={addMemberDialogVisible}
        style={{ width: "450px" }}
        header="Add Member"
        modal
        className="p-fluid"
        footer={addMemberDialogFooter}
        onHide={() => {
          setAddMemberDialogVisible(false);
          setSelectedUserId("");
        }}
      >
        {isUsersLoading ? (
          <div className="field">
            <p>Loading users...</p>
          </div>
        ) : hasUsersError ? (
          <div className="field">
            <p className="text-red-600">
              Error loading users. Please try again.
            </p>
          </div>
        ) : availableUsers.length === 0 ? (
          <div className="field">
            <p className="text-gray-600">
              {allUsers.length === 0
                ? "No users found in the system."
                : "All users are already members of this group."}
            </p>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="user">User</label>
            <Dropdown
              id="user"
              value={selectedUserId}
              options={userOptions}
              onChange={(e) => setSelectedUserId(e.value)}
              placeholder="Select User"
              filter
              className={submitted && !selectedUserId ? "p-invalid" : ""}
            />
            {submitted && !selectedUserId && (
              <small className="p-error">User is required.</small>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}

